import type { Vec2 } from '@store/handStore';
import {
  FINGER_MCPS,
  FINGER_PIPS,
  FINGER_TIPS,
  LM,
  dist,
  fingerCurl,
  meanCurl,
  type FingerName,
} from '@engine/hand/landmarks';
import type { CapturedBucket, CapturedFrame } from './recorder';

export interface Stats {
  n: number;
  min: number;
  p5: number;
  p50: number;
  p95: number;
  max: number;
  mean: number;
  std: number;
}

export interface BucketStats {
  id: string;
  hand: string;
  frames: { left: number; right: number };
  metrics: Record<string, Stats>;
}

function computeStats(samples: number[]): Stats {
  const n = samples.length;
  if (n === 0) {
    return { n: 0, min: 0, p5: 0, p50: 0, p95: 0, max: 0, mean: 0, std: 0 };
  }
  const sorted = [...samples].sort((a, b) => a - b);
  const at = (q: number) => sorted[Math.min(n - 1, Math.max(0, Math.floor(q * (n - 1))))]!;
  let sum = 0;
  for (const s of samples) sum += s;
  const mean = sum / n;
  let varSum = 0;
  for (const s of samples) varSum += (s - mean) ** 2;
  return {
    n,
    min: sorted[0]!,
    p5: at(0.05),
    p50: at(0.5),
    p95: at(0.95),
    max: sorted[n - 1]!,
    mean,
    std: Math.sqrt(varSum / n),
  };
}

/**
 * Derive every per-frame metric we care about from a single 21-landmark
 * pose. Velocities need a prior frame, so those are emitted pair-wise by
 * `framePairMetrics` below.
 */
function frameMetrics(lm: Vec2[]): Record<string, number> {
  const out: Record<string, number> = {};

  const wrist = lm[LM.WRIST]!;
  const midMcp = lm[LM.MIDDLE_MCP]!;
  const idxMcp = lm[LM.INDEX_MCP]!;
  const pinkyMcp = lm[LM.PINKY_MCP]!;

  const palmW = dist(idxMcp, pinkyMcp);
  const palmH = dist(wrist, midMcp);
  out.palmW = palmW;
  out.palmH = palmH;
  out.palmW_over_H = palmH > 1e-6 ? palmW / palmH : 0;

  const dxWM = Math.abs(midMcp.x - wrist.x);
  const dyWM = wrist.y - midMcp.y;
  out.upright_dy_over_dx = dxWM > 1e-6 ? dyWM / dxWM : 99;

  out.mean_curl = meanCurl(lm);

  const fingers: FingerName[] = ['thumb', 'index', 'middle', 'ring', 'pinky'];
  for (const f of fingers) {
    out[`curl_${f}`] = fingerCurl(lm, f);
    const mcp = lm[FINGER_MCPS[f]]!;
    const pip = lm[FINGER_PIPS[f]]!;
    const tip = lm[FINGER_TIPS[f]]!;
    const pipToMcp = dist(pip, mcp);
    out[`tip_over_pip_${f}`] = pipToMcp > 1e-6 ? dist(tip, mcp) / pipToMcp : 99;
  }

  // Tip X/Y spread for index/middle/ring.
  const t8 = lm[LM.INDEX_TIP]!;
  const t12 = lm[LM.MIDDLE_TIP]!;
  const t16 = lm[LM.RING_TIP]!;
  out.tips_x_spread = Math.max(t8.x, t12.x, t16.x) - Math.min(t8.x, t12.x, t16.x);
  out.tips_y_spread = Math.max(t8.y, t12.y, t16.y) - Math.min(t8.y, t12.y, t16.y);

  // Bend alignment — upper-joint Y-spread on each of the 3 layers.
  const layerSpread = (a: number, b: number, c: number): number => {
    const ys = [lm[a]!.y, lm[b]!.y, lm[c]!.y];
    return Math.max(...ys) - Math.min(...ys);
  };
  out.bend_yspread_dip = layerSpread(LM.INDEX_DIP, LM.MIDDLE_DIP, LM.RING_DIP);
  out.bend_yspread_pip = layerSpread(LM.INDEX_PIP, LM.MIDDLE_PIP, LM.RING_PIP);
  out.bend_yspread_mcp = layerSpread(LM.INDEX_MCP, LM.MIDDLE_MCP, LM.RING_MCP);

  return out;
}

/** Per-frame metrics that need the prior frame for velocity. */
function framePairMetrics(
  prev: CapturedFrame,
  cur: CapturedFrame,
): Record<string, number> {
  const dt = Math.max(1, cur.t - prev.t) / 1000;
  const out: Record<string, number> = {};
  const joints: Array<[string, number]> = [
    ['thumb_tip', LM.THUMB_TIP],
    ['thumb_ip', LM.THUMB_IP],
    ['thumb_mcp', LM.THUMB_MCP],
    ['index_tip', LM.INDEX_TIP],
    ['index_pip', LM.INDEX_PIP],
    ['index_mcp', LM.INDEX_MCP],
    ['middle_tip', LM.MIDDLE_TIP],
    ['middle_pip', LM.MIDDLE_PIP],
    ['middle_mcp', LM.MIDDLE_MCP],
  ];
  for (const [name, idx] of joints) {
    const a = prev.landmarks[idx]!;
    const b = cur.landmarks[idx]!;
    out[`velY_${name}`] = (b.y - a.y) / dt;
    out[`absVelY_${name}`] = Math.abs((b.y - a.y) / dt);
  }
  return out;
}

function analyzeHandFrames(frames: CapturedFrame[]): Record<string, number[]> {
  const accum: Record<string, number[]> = {};
  const push = (k: string, v: number) => {
    if (!accum[k]) accum[k] = [];
    accum[k].push(v);
  };

  for (let i = 0; i < frames.length; i++) {
    const f = frames[i]!;
    const m = frameMetrics(f.landmarks);
    for (const k in m) push(k, m[k]!);
    if (i > 0) {
      const pm = framePairMetrics(frames[i - 1]!, f);
      for (const k in pm) push(k, pm[k]!);
    }
  }
  return accum;
}

export function analyzeBuckets(buckets: readonly CapturedBucket[]): BucketStats[] {
  const out: BucketStats[] = [];
  for (const b of buckets) {
    const frames =
      b.hand === 'left' ? b.left : b.hand === 'right' ? b.right : [...b.left, ...b.right];
    const accum = analyzeHandFrames(frames);
    const metrics: Record<string, Stats> = {};
    for (const k in accum) metrics[k] = computeStats(accum[k]!);
    out.push({
      id: b.id,
      hand: b.hand,
      frames: { left: b.left.length, right: b.right.length },
      metrics,
    });
  }
  return out;
}

/**
 * Pretty-print a compact table to the console, grouped by metric rather
 * than by bucket — makes threshold separation obvious (same column = same
 * metric across all gestures).
 */
export function logStatsTable(stats: BucketStats[], keyMetrics: readonly string[]): void {
  const rows = stats.map((s) => {
    const row: Record<string, string> = {
      id: s.id,
      n: `${s.frames.left}/${s.frames.right}`,
    };
    for (const m of keyMetrics) {
      const st = s.metrics[m];
      row[m] = st ? `${st.p5.toFixed(3)}|${st.p50.toFixed(3)}|${st.p95.toFixed(3)}` : '—';
    }
    return row;
  });
  // eslint-disable-next-line no-console
  console.table(rows);
}

/** The metrics we print live so the user can eyeball separation. */
export const LIVE_KEY_METRICS = [
  'mean_curl',
  'tip_over_pip_index',
  'absVelY_index_tip',
  'absVelY_index_mcp',
  'bend_yspread_pip',
  'palmW_over_H',
] as const;
